/** Thai labels + small pure helpers for the knowledge page (tested in test/knowledge.spec.ts). */
import type { KbFact, KbLogEntry, KbState, KbStatus } from '../../api/kb'
import { isYmd, ROUND_STATUS_LABEL, thaiDate } from '../../lib/menuRound'

export const STATUS_LABEL: Record<KbStatus, string> = {
  approved: 'ใช้อยู่',
  pending_approval: 'รอเจ้าของอนุมัติ',
  rejected: 'ไม่อนุมัติ',
  retired: 'ปิดใช้',
}

export const FIELD_LABEL: Record<string, string> = {
  category: 'หมวด',
  title: 'หัวข้อ',
  text: 'ข้อความ',
  value: 'ค่า',
  status: 'สถานะ',
  intent: 'หัวข้อคำถาม',
  grp: 'กลุ่ม',
  answer: 'คำตอบ',
  question_examples: 'ตัวอย่างคำถาม',
  // เมนูรอบนี้ (entity menu): small summaries the server logs
  valid_from: 'ตั้งแต่',
  valid_to: 'ถึง',
  dishes: 'จำนวนเมนู',
  overrides: 'เมนูที่แก้',
  poster_media_id: 'โปสเตอร์',
  notes: 'หมายเหตุ',
}

export const ENTITY_LABEL = { fact: 'ข้อมูลร้าน', answer: 'คำตอบ', rule: 'กติกา', menu: 'เมนูรอบนี้' } as const

export const VALUE_TOKEN = '{ค่า}'
export const LINK_TOKEN = '{ลิงก์สั่งซื้อ}'

export interface Segment {
  kind: 'text' | 'value' | 'link'
  text: string
}

/** A linked fact's value the way the AI writes it: 22:00 → 22.00 น. · 120 → 120 วัน */
export function valueText(value: string | null, kind: KbFact['value_kind']): string {
  if (value === null) return ''
  return kind === 'time' ? `${value.replace(':', '.')} น.` : `${value} วัน`
}

/** Template → segments so the value / link can be highlighted (the sentence the AI gets). */
export function factSegments(text: string, value: string | null, kind: KbFact['value_kind'], link: string): Segment[] {
  const shown = value === null ? VALUE_TOKEN : kind === 'time' ? value.replace(':', '.') : value
  const out: Segment[] = []
  const re = /\{ค่า\}|\{ลิงก์สั่งซื้อ\}/g
  let last = 0
  for (const m of text.matchAll(re)) {
    if (m.index! > last) out.push({ kind: 'text', text: text.slice(last, m.index) })
    out.push(m[0] === VALUE_TOKEN ? { kind: 'value', text: shown } : { kind: 'link', text: link })
    last = m.index! + m[0].length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
  return out
}

/** Numbers as the guardrail compares them (22.00 / 22:00 → "22:00", 1,093 → "1093"). */
export function numbersIn(text: string): Set<string> {
  const out = new Set<string>()
  for (const m of text.matchAll(/(\d{1,2})[:.](\d{2})(?!\d)|\d[\d,]*/g)) {
    if (m[1] !== undefined) out.add(`${+m[1]}:${m[2]}`)
    else out.add(m[0].replace(/,/g, '').replace(/^0+(?=\d)/, ''))
  }
  return out
}

/** The shop facts whose numbers show up in the draft (i.e. the draft leaned on them). */
export function factsBehindDraft(draft: string | null, facts: { id: string; text: string }[]): Set<string> {
  if (!draft) return new Set()
  const inDraft = numbersIn(draft)
  const hit = new Set<string>()
  for (const f of facts) {
    for (const n of numbersIn(f.text)) {
      if (inDraft.has(n)) {
        hit.add(f.id)
        break
      }
    }
  }
  return hit
}

/** Split a draft so "[เช็คตัวเลข]" / "[ลิงก์]" marks can be shown in red. */
export function draftSegments(draft: string): { mark: boolean; text: string }[] {
  const out: { mark: boolean; text: string }[] = []
  let last = 0
  for (const m of draft.matchAll(/\[เช็คตัวเลข\]|\[ลิงก์\]/g)) {
    if (m.index! > last) out.push({ mark: false, text: draft.slice(last, m.index) })
    out.push({ mark: true, text: m[0] })
    last = m.index! + m[0].length
  }
  if (last < draft.length) out.push({ mark: false, text: draft.slice(last) })
  return out
}

/** One field's value for a person to read (labels instead of ids). */
export function fieldValue(field: string, v: unknown, st: KbState | null): string {
  if (v === null || v === undefined || v === '') return '—'
  if (field === 'status') return STATUS_LABEL[v as KbStatus] ?? ROUND_STATUS_LABEL[String(v)] ?? String(v)
  if ((field === 'valid_from' || field === 'valid_to') && typeof v === 'string' && isYmd(v)) return thaiDate(v)
  if ((field === 'dishes' || field === 'overrides') && typeof v === 'number') return `${v} เมนู`
  if (field === 'poster_media_id') return 'มีโปสเตอร์'
  if (field === 'category') return st?.categories.find((c) => c.id === v)?.label ?? String(v)
  if (field === 'intent') return st?.intents.find((i) => i.id === v)?.label ?? String(v)
  if (field === 'grp') return st?.groups.find((g) => g.id === v)?.label ?? String(v)
  if (field === 'question_examples' && Array.isArray(v)) return v.length ? v.join(' · ') : '—'
  if (typeof v === 'object') {
    const n = v as { new_ids?: unknown[]; recommended_ids?: unknown[]; text?: string }
    if (field === 'notes' && (Array.isArray(n.new_ids) || Array.isArray(n.recommended_ids))) {
      return `เมนูใหม่ ${n.new_ids?.length ?? 0} · แนะนำ ${n.recommended_ids?.length ?? 0}${n.text ? ` · ${n.text}` : ''}`
    }
    return Array.isArray(v) ? v.join(' · ') : JSON.stringify(v)
  }
  return String(v)
}

export interface DiffRow {
  field: string
  label: string
  old: string | null
  new: string
}

const MAIN_FIELD = ['text', 'answer', 'title']

/** What a log entry changed, as rows "label: old → new". A new row shows only its main text. */
export function logDiff(e: KbLogEntry, st: KbState | null): DiffRow[] {
  const nw = e.new ?? {}
  const old = e.old
  if (e.action === 'create' || (e.action === 'propose' && old === null)) {
    const f = MAIN_FIELD.find((k) => typeof nw[k] === 'string' && nw[k])
    if (!f || nw[f] === e.label) return [] // a rule's text is already its label
    return [{ field: f, label: FIELD_LABEL[f] ?? f, old: null, new: fieldValue(f, nw[f], st) }]
  }
  const keys = Object.keys(nw).filter((k) => JSON.stringify(nw[k]) !== JSON.stringify(old?.[k]))
  return keys
    .filter((k) => k !== 'grp' || !keys.includes('intent'))
    .map((k) => ({ field: k, label: FIELD_LABEL[k] ?? k, old: old && k in old ? fieldValue(k, old[k], st) : null, new: fieldValue(k, nw[k], st) }))
}

/** "อนุมัติ" / "เสนอแก้" / "ขอปิดใช้" … */
export function actionText(e: KbLogEntry): string {
  switch (e.action) {
    case 'create':
      return 'เพิ่ม'
    case 'edit':
      return 'แก้'
    case 'propose':
      if (e.old === null) return 'เสนอเพิ่ม'
      if (e.new && e.new.status === 'retired' && Object.keys(e.new).length === 1) return 'ขอปิดใช้'
      return 'เสนอแก้'
    case 'approve':
      return 'อนุมัติ'
    case 'reject':
      return 'ไม่อนุมัติ'
    case 'withdraw':
      return 'ยกเลิกข้อเสนอ'
    case 'retire':
      return 'ปิดใช้'
    case 'restore':
      return 'เปิดใช้อีกครั้ง'
    case 'undo':
      return 'ย้อนกลับ'
  }
}

export const fmtCount = (n: number): string => new Intl.NumberFormat('th-TH').format(n)
